import { useLayoutEffect, useMemo, useRef, type KeyboardEvent } from "react";
import { Table, type Column } from "@legal-500/legal500-design-system";
import { SortableHeader } from "./SortableHeader.js";
import styles from "./DataTable.module.css";

type DataTableProps<T> = {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  size?: "small" | "medium" | "large";
  maxHeight?: string;
  stickyHeader?: boolean;
  rowKey?: keyof T;
  sortableColumnKeys?: string[];
  sortFieldByAccessor?: Partial<Record<string, string>>;
  sortField?: string | null;
  sortDirection?: "asc" | "desc";
  onSort?: (field: string) => void;
  rowClassName?: (row: T) => string;
  rowLabel?: (row: T) => string;
};

export function DataTable<T>({
  data,
  columns,
  onRowClick,
  emptyMessage = "No data found matching your criteria.",
  className,
  size = "small",
  maxHeight,
  stickyHeader,
  rowKey,
  sortableColumnKeys,
  sortFieldByAccessor,
  sortField,
  sortDirection = "asc",
  onSort,
  rowClassName,
  rowLabel,
}: DataTableProps<T>) {
  const wrapper = useRef<HTMLDivElement>(null);

  const enhancedColumns = useMemo(() => {
    if (!sortableColumnKeys || !onSort) {
      return columns;
    }

    return columns.map(column => {
      const accessorKey = String(column.accessor);
      const field = sortFieldByAccessor?.[accessorKey] ?? accessorKey;
      if (!sortableColumnKeys.includes(field)) {
        return column;
      }

      return {
        ...column,
        header: (
          <SortableHeader
            label={column.header as string}
            field={field}
            sortField={sortField ?? null}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        ) as unknown as string,
      };
    });
  }, [columns, sortableColumnKeys, sortFieldByAccessor, sortField, sortDirection, onSort]);

  const tableData = useMemo(() => {
    if (!rowKey) return data;
    return data.map(row => ({ ...row, id: row[rowKey] }));
  }, [data, rowKey]);

  useLayoutEffect(() => {
    const rows = wrapper.current?.querySelectorAll<HTMLTableRowElement>("tbody > tr") ?? [];
    rows.forEach((element, index) => {
      const row = data[index];
      if (!onRowClick || !row) {
        element.removeAttribute("tabindex");
        element.removeAttribute("aria-label");
        return;
      }
      element.tabIndex = 0;
      const label = rowLabel?.(row);
      if (label) element.setAttribute("aria-label", label);
      else element.removeAttribute("aria-label");
    });
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onRowClick || (event.key !== "Enter" && event.key !== " ")) return;
    const target = event.target;
    if (!(target instanceof HTMLTableRowElement) || target.parentElement?.tagName !== "TBODY") {
      return;
    }
    const row = tableData[target.sectionRowIndex] as T | undefined;
    if (!row) return;
    event.preventDefault();
    onRowClick(row);
  };

  if (data.length === 0) {
    return <div className={styles.emptyText}>{emptyMessage}</div>;
  }

  return (
    <div ref={wrapper} className={styles.tableWrapper} onKeyDown={handleKeyDown}>
      <Table
        data={tableData as unknown as { id: string | number }[]}
        columns={enhancedColumns as unknown as Column<{ id: string | number }>[]}
        onRowClick={onRowClick as unknown as (row: { id: string | number }) => void}
        ariaLabel="Data table"
        size={size}
        className={className}
        maxHeight={maxHeight}
        stickyHeader={stickyHeader}
        rowClassName={rowClassName as unknown as (row: { id: string | number }) => string}
      />
    </div>
  );
}
